import { useCallback, useMemo } from 'react';

import classNames from 'classnames';

import NexLogo from 'assets/logo/NexLogo';

import { MdHelp, MdReportProblem } from 'react-icons/md';

import { ALPHA_NUMERIC } from 'utils/unmask';
import {
  convertToBRL,
  formatDateToLocaleDateString,
  getPartsFromDate,
} from 'utils/form';

import { ConsumptionGroupsType } from 'consumerUnits/model/consumerUnit';

import { EconomyReportDataProps } from 'economyReport/pages/EconomyReport';

import EconomyReportGroupB from 'assets/background/economy-report/EconomyReportGroupB';
import EconomyReportGroupA from 'assets/background/economy-report/EconomyReportGroupA';

import { TariffFlagModalityModalityFlagType } from 'tariffFlagTariffs/models/tariffFlagModality';

type MonthAndYearProps = {
  date: string;
  spliter: '/' | '-' | '';
  options: Intl.DateTimeFormatOptions;
};

export default function FirstPageEconomyReport({
  economyReportData,
}: EconomyReportDataProps) {
  const {
    usedCredits = '0',
    economyValue = '0',
    economyReports = [],
    receivedCredits = '0',
    usedCreditsOnPeak = '0',
    usedCreditsOffPeak = '0',
    chargeTotalCharged = '0',
    accumulatedCredits = '0',
    receivedCreditsOnPeak = '0',
    receivedCreditsOffPeak = '0',
    generationUnitLegalName = '-',
    approximateChargeAmount = '0',
    consumerUnitAddressCity = '-',
    consumerUnitAddressState = '-',
    accumulatedCreditsOnPeak = '0',
    billTotalValue = '0',
    consumerUnitAddressStreet = '-',
    accumulatedCreditsOffPeak = '0',
    generationUnitAddressCity = '-',
    generationUnitAddressState = '-',
    cooperativeMemberLegalName = '-',
    cooperativeMemberDocumentId = '-',
    generationUnitAddressStreet = '-',
    generationUnitPowerCapacity = '0',
    tariffFlagModalityModalityFlag = '-',
    chargeDueDate = new Date().getTime(),
    consumerUnitConsumptionGroupType = '-',
    billReferenceDate = new Date().getTime(),
    apportionmentCycleConsumerUnitPercentual = '0',
    consumerUnitPowerDistributionUnitIdentifier = '-',
    billTotalValueAddChargeTotalCharged = '0',
  } = economyReportData || {};

  const HAS_CONSUMPTION_GROUP_TYPE_B = useMemo(
    () => consumerUnitConsumptionGroupType === ConsumptionGroupsType.B,
    [consumerUnitConsumptionGroupType]
  );

  const getMonthAndYear = useCallback(
    ({ date, spliter, options }: MonthAndYearProps) =>
      `${getPartsFromDate(date, options)
        .toUpperCase()
        .replace('.', '')}${spliter}${getPartsFromDate(date, {
        year: 'numeric',
        timeZone: 'UTC',
      })}`,
    []
  );

  const getTariffFlagModalityFlag = useCallback(
    () =>
      [
        {
          key: TariffFlagModalityModalityFlagType.GREEN,
          flag: 'Bandeira Verde',
          color: 'border-primary',
        },
        {
          key: TariffFlagModalityModalityFlagType.YELLOW,
          flag: 'Bandeira Amarela',
          color: 'border-yellow-500',
        },
        {
          key: TariffFlagModalityModalityFlagType.RED_ON_PEAK,
          flag: 'Bandeira Vermelha PT1',
          color: 'border-red-a1',
        },
        {
          key: TariffFlagModalityModalityFlagType.RED_ON_PEAK_TWO,
          flag: 'Bandeira Vermelha PT2',
          color: 'border-red-a1',
        },
      ].find(({ key }) => key === tariffFlagModalityModalityFlag),
    [tariffFlagModalityModalityFlag]
  );

  const HAS_CONSUMER_UNIT_INFO = useMemo(
    () => [
      {
        key: 'UNIDADE CONSUMIDORA',
        value: consumerUnitPowerDistributionUnitIdentifier,
      },
      {
        key: 'CNPJ/CPF',
        value: cooperativeMemberDocumentId,
      },
      {
        key: 'ENDEREÇO',
        value: `${consumerUnitAddressStreet}, ${consumerUnitAddressCity} - ${consumerUnitAddressState}`,
      },
    ],
    [
      consumerUnitAddressCity,
      consumerUnitAddressState,
      consumerUnitAddressStreet,
      cooperativeMemberDocumentId,
      consumerUnitPowerDistributionUnitIdentifier,
    ]
  );

  const HAS_GENERATION_UNIT_INFO = useMemo(
    () => [
      {
        key: 'POTÊNCIA INSTALADA',
        value: +generationUnitPowerCapacity / 1000 + ' MW',
      },
      {
        key: 'PERCENTUAL ALUGADO',
        value: apportionmentCycleConsumerUnitPercentual + '%',
      },
      {
        key: 'ENDEREÇO',
        value: `${generationUnitAddressStreet}, ${generationUnitAddressCity} - ${generationUnitAddressState}`,
      },
    ],
    [
      generationUnitAddressCity,
      generationUnitAddressState,
      generationUnitPowerCapacity,
      generationUnitAddressStreet,
      apportionmentCycleConsumerUnitPercentual,
    ]
  );

  const HAS_BILLING_INFO = useMemo(
    () => [
      {
        key: 'IDENTIFICADOR DO RELATÓRIO',
        value: `RE-${ALPHA_NUMERIC(
          consumerUnitPowerDistributionUnitIdentifier
        )}-${ALPHA_NUMERIC(
          formatDateToLocaleDateString(billReferenceDate as string, {
            year: '2-digit',
            month: '2-digit',
          })
        )}`,
      },
      {
        key: 'MÊS DE REFERÊNCIA',
        value: getMonthAndYear({
          spliter: '/',
          options: {
            month: 'short',
            timeZone: 'UTC',
          },
          date: billReferenceDate as string,
        }),
      },
      {
        key: 'BANDEIRA TARIFÁRIA',
        value: getTariffFlagModalityFlag()?.flag,
      },
    ],
    [
      getMonthAndYear,
      billReferenceDate,
      getTariffFlagModalityFlag,
      consumerUnitPowerDistributionUnitIdentifier,
    ]
  );

  return (
    <div
      id="economy-report-first-page"
      className="flex flex-col w-full items-center justify-start h-screen bg-white font-rubik relative"
      style={{
        gap: '.75rem',
        width: '210mm',
        height: '297mm',
        padding: '1.5rem 2.65rem',
      }}
    >
      <header className="w-full flex items-center justify-between pb-6">
        <h1
          className="font-bold"
          id="economy-report-title"
          style={{
            fontSize: '1.6rem',
            lineHeight: '2rem',
            letterSpacing: '2.5px',
          }}
        >
          RELATÓRIO DE ECONOMIA
        </h1>
        <NexLogo />
      </header>
      <section className="flex w-full items-start justify-between pl-6 gap-6">
        <div className="flex flex-col items-start justify-start gap-6">
          <div className="flex items-center justify-start">
            <h1
              className="font-bold"
              style={{
                maxWidth: '350px',
                fontSize: '1.5rem',
                lineHeight: '2rem',
              }}
            >
              {cooperativeMemberLegalName}
            </h1>
          </div>
          <div className="flex flex-col items-start justify-center gap-1">
            {HAS_CONSUMER_UNIT_INFO.map((item) => (
              <p
                key={item.key}
                className="font-light"
                style={{
                  letterSpacing: '.05rem',
                  fontSize: '.7rem',
                }}
              >
                {item.key}:{' '}
                <span
                  className="font-bold"
                  style={{
                    letterSpacing: 'normal',
                  }}
                >
                  {item.value || '-'}
                </span>
              </p>
            ))}
          </div>
        </div>
        <div
          id="economy-report-billing-info"
          className={classNames('flex flex-col bg-gray-dark200 gap-4', {
            [getTariffFlagModalityFlag()?.color as string]:
              !!getTariffFlagModalityFlag(),
          })}
          style={{
            borderLeftWidth: '3px',
            padding: '1.5rem 2rem',
          }}
        >
          {HAS_BILLING_INFO.map((item) => (
            <div key={item.key}>
              <p
                className="font-light"
                style={{
                  fontSize: '.7rem',
                  lineHeight: '.5rem',
                  letterSpacing: '.05rem',
                }}
              >
                {item.key}:{' '}
              </p>
              <span
                className="font-bold"
                style={{
                  fontSize: '.7rem',
                  letterSpacing: 'normal',
                }}
              >
                {item.value || '-'}
              </span>
            </div>
          ))}
        </div>
      </section>
      <section
        className="flex flex-col w-full items-start justify-start"
        style={{
          padding: '1rem 0',
        }}
      >
        <div
          className="flex w-full bg-gray-dark200 relative"
          style={{
            padding: '.5rem 1rem .5rem 1rem',
          }}
        >
          <div
            className="flex flex-col"
            style={{
              width: HAS_CONSUMPTION_GROUP_TYPE_B ? '625px' : 'inherit',
            }}
          >
            <div
              className="flex flex-col gap-1"
              style={{
                padding: '1rem 1rem .5rem 1rem',
              }}
            >
              <div
                className={classNames(
                  'flex items-center justify-between border-b-2 border-gray gap-4',
                  {
                    'grid grid-cols-3': !HAS_CONSUMPTION_GROUP_TYPE_B,
                  }
                )}
              >
                <h2
                  id="economy-report-credits-title"
                  className="leading-5 pb-1 font-bold"
                  style={{
                    width: 'max-content',
                    fontSize: '0.7rem',
                    lineHeight: '1rem',
                  }}
                >
                  CARTEIRA DE <br /> CRÉDITOS
                </h2>
                {!HAS_CONSUMPTION_GROUP_TYPE_B && (
                  <>
                    <h3
                      id="economy-report-credits-title"
                      className="font-bold text-center leading-4 font-bold"
                      style={{
                        fontSize: '0.7rem',
                      }}
                    >
                      PONTA
                    </h3>
                    <h3
                      id="economy-report-credits-title"
                      className="font-bold text-center leading-4 font-bold"
                      style={{
                        fontSize: '0.7rem',
                      }}
                    >
                      FORA <br />
                      PONTA
                    </h3>
                  </>
                )}
              </div>
              <div
                className={classNames(
                  'flex items-center justify-between gap-4 pt-1 pb-1',
                  {
                    'grid grid-cols-3': !HAS_CONSUMPTION_GROUP_TYPE_B,
                  }
                )}
              >
                <h3
                  id="economy-report-credits-title"
                  className="font-medium leading-4"
                  style={{
                    fontSize: '0.7rem',
                  }}
                >
                  CRÉDITOS <br /> ACUMULADOS¹
                </h3>
                {!HAS_CONSUMPTION_GROUP_TYPE_B ? (
                  <>
                    <p
                      id="economy-report-credits-value"
                      className="font-normal text-center"
                      style={{
                        fontSize: '1.3rem',
                      }}
                    >
                      {Math.round(+accumulatedCreditsOnPeak)}
                    </p>
                    <p
                      id="economy-report-credits-value"
                      className="font-normal text-center"
                      style={{
                        fontSize: '1.3rem',
                      }}
                    >
                      {Math.round(+accumulatedCreditsOffPeak)}
                    </p>
                  </>
                ) : (
                  <p
                    id="economy-report-credits-value"
                    className="font-normal text-center"
                    style={{
                      fontSize: '1.3rem',
                    }}
                  >
                    {Math.round(+accumulatedCreditsOffPeak || +accumulatedCredits)}
                  </p>
                )}
              </div>
              <div
                className={classNames(
                  'flex items-center justify-between border-b-2 border-t-2 border-gray pt-2 pb-2 gap-4',
                  {
                    'grid grid-cols-3': !HAS_CONSUMPTION_GROUP_TYPE_B,
                  }
                )}
              >
                <h3
                  id="economy-report-credits-title"
                  className="font-medium leading-4"
                  style={{
                    fontSize: '0.7rem',
                  }}
                >
                  CRÉDITOS <br /> RECEBIDOS²
                </h3>
                {!HAS_CONSUMPTION_GROUP_TYPE_B ? (
                  <>
                    <p
                      id="economy-report-credits-value"
                      className="font-normal text-center"
                      style={{
                        fontSize: '1.3rem',
                      }}
                    >
                      {Math.round(+receivedCreditsOnPeak)}
                    </p>
                    <p
                      id="economy-report-credits-value"
                      className="font-normal text-center"
                      style={{
                        fontSize: '1.3rem',
                      }}
                    >
                      {Math.round(+receivedCreditsOffPeak)}
                    </p>
                  </>
                ) : (
                  <p
                    id="economy-report-credits-value"
                    className="font-normal text-center"
                    style={{
                      fontSize: '1.3rem',
                    }}
                  >
                    {Math.round(+receivedCreditsOffPeak || +receivedCredits)}
                  </p>
                )}
              </div>
              <div
                className={classNames(
                  'flex items-center justify-between gap-4 pt-1',
                  {
                    'grid grid-cols-3': !HAS_CONSUMPTION_GROUP_TYPE_B,
                  }
                )}
              >
                <h3
                  id="economy-report-credits-title"
                  className="font-medium leading-4"
                  style={{
                    fontSize: '0.7rem',
                  }}
                >
                  CRÉDITOS <br /> UTILIZADOS²
                </h3>
                {!HAS_CONSUMPTION_GROUP_TYPE_B ? (
                  <>
                    <p
                      id="economy-report-credits-value"
                      className="font-normal text-center"
                      style={{
                        fontSize: '1.3rem',
                      }}
                    >
                      {Math.round(+usedCreditsOnPeak)}
                    </p>
                    <p
                      id="economy-report-credits-value"
                      className="font-normal text-center"
                      style={{
                        fontSize: '1.3rem',
                      }}
                    >
                      {Math.round(+usedCreditsOffPeak)}
                    </p>
                  </>
                ) : (
                  <p
                    id="economy-report-credits-value"
                    className="font-normal text-center"
                    style={{
                      fontSize: '1.3rem',
                    }}
                  >
                    {Math.round(+usedCreditsOffPeak || +usedCredits)}
                  </p>
                )}
              </div>
            </div>
            <div
              className={classNames(
                'flex items-center justify-between bg-primary gap-4',
                {
                  'grid grid-cols-3': !HAS_CONSUMPTION_GROUP_TYPE_B,
                }
              )}
              style={{
                padding: '.75rem 1rem',
              }}
            >
              <h3
                id="economy-report-credits-title"
                className="font-semibold leading-4"
                style={{
                  fontSize: '0.7rem',
                }}
              >
                CRÉDITOS <br /> RESTANTES²
              </h3>
              {!HAS_CONSUMPTION_GROUP_TYPE_B ? (
                <>
                  <p
                    id="economy-report-credits-value"
                    className="font-semibold text-center"
                    style={{
                      fontSize: '1.3rem',
                    }}
                  >
                    {Math.round(
                      +accumulatedCreditsOnPeak +
                        +receivedCreditsOnPeak -
                        +usedCreditsOnPeak
                    )}
                  </p>
                  <p
                    id="economy-report-credits-value"
                    className="font-semibold text-center"
                    style={{
                      fontSize: '1.3rem',
                    }}
                  >
                    {Math.round(
                      +accumulatedCreditsOffPeak +
                        +receivedCreditsOffPeak -
                        +usedCreditsOffPeak
                    )}
                  </p>
                </>
              ) : (
                <p
                  id="economy-report-credits-value"
                  className="font-normal text-center"
                  style={{
                    fontSize: '1.3rem',
                  }}
                >
                  {Math.round(+accumulatedCredits + +receivedCredits - +usedCredits)}
                </p>
              )}
            </div>
            <div
              className="flex"
              style={{
                padding: '.5rem 1rem .25rem 1rem',
              }}
            >
              <span id="economy-report-credits-legend" className="text-xs leading-4">
                ¹ Saldo de créditos referente à períodos anteriores. <br />² Créditos
                referentes ao ciclo de faturamento atual.
              </span>
            </div>
          </div>
          <div className="flex flex-col w-full p-4 gap-2">
            <h1
              id="economy-report-generation-unit-name"
              className="font-bold pb-2 leading-6"
              style={{
                maxWidth: '350px',
                fontSize: '1.5rem',
              }}
            >
              {generationUnitLegalName}
            </h1>
            {HAS_GENERATION_UNIT_INFO.map((item) => (
              <div key={item.key}>
                <h3
                  className="font-semibold leading-4"
                  style={{
                    fontSize: '0.7rem',
                  }}
                >
                  {item.key}
                </h3>
                <p
                  style={{
                    fontSize: '0.7rem',
                  }}
                >
                  {item.value}
                </p>
              </div>
            ))}
          </div>
          {HAS_CONSUMPTION_GROUP_TYPE_B ? (
            <EconomyReportGroupB className="absolute bottom-0 right-0" />
          ) : (
            <EconomyReportGroupA className="absolute bottom-0 right-0" />
          )}
        </div>
        <div
          className="flex gap-6 items-center justify-start bg-gray w-full"
          style={{
            padding: '1rem 1.5rem',
          }}
        >
          <div className="flex items-center justify-start gap-2">
            <MdReportProblem size={15} />
            <h1
              id="economy-report-notice-title"
              className="font-bold"
              style={{
                lineHeight: '1rem',
                fontSize: '1rem',
              }}
            >
              AVISOS
            </h1>
          </div>
          <p
            id="economy-report-notice-message"
            className="w-full flex"
            style={{
              fontSize: '0.65rem',
              lineHeight: '.75rem',
            }}
          >
            Nenhum aviso disponível
          </p>
        </div>
      </section>
      <section className="flex w-full items-start justify-between">
        <div className="flex flex-col items-start justify-start gap-4">
          <div
            className="flex flex-col items-start justify-start w-full gap-4"
            style={{
              maxWidth: '425px',
            }}
          >
            <h1
              id="economy-report-bill-title"
              className="font-bold leading-6"
              style={{
                padding: '0 1rem',
                fontSize: '1.5rem',
              }}
            >
              Entenda a sua fatura com a NEX
            </h1>
            <div
              className="flex items-center w-full justify-between"
              style={{
                padding: '0 1rem',
              }}
            >
              <h3
                className="font-medium leading-4"
                style={{
                  fontSize: '0.7rem',
                }}
              >
                FATURA ATUAL DA <br /> CONCESSIONÁRIA
              </h3>
              <h2
                style={{
                  fontSize: '1.3rem',
                }}
              >
                {convertToBRL(billTotalValue)}
              </h2>
            </div>
            <div
              className="flex items-center w-full justify-between border-primary"
              style={{
                borderWidth: '3px',
                padding: '.5rem 1rem',
              }}
            >
              <div className="flex flex-col items-start justify-start">
                <h3
                  id="economy-report-bill-title"
                  className="font-medium leading-4"
                  style={{
                    fontSize: '0.7rem',
                  }}
                >
                  FATURA DA NEX ENERGY
                </h3>
                <p
                  id="economy-report-bill-due-date"
                  style={{
                    fontSize: '0.7rem',
                  }}
                >
                  Vencimento -{' '}
                  {getMonthAndYear({
                    spliter: '/',
                    options: {
                      day: '2-digit',
                      timeZone: 'UTC',
                      month: '2-digit',
                    },
                    date: chargeDueDate as string,
                  })}
                </p>
              </div>
              <h2
                id="economy-report-bill-value"
                style={{
                  fontSize: '1.3rem',
                }}
              >
                {convertToBRL(chargeTotalCharged)}
              </h2>
            </div>
          </div>
          <div
            className="flex flex-col items-start justify-start w-full gap-4"
            style={{
              maxWidth: '425px',
            }}
          >
            <h1
              id="economy-report-bill-title"
              className="font-bold leading-6"
              style={{
                fontSize: '1.5rem',
                padding: '0 1rem',
              }}
            >
              Sua economia esse mês
            </h1>
            <div
              className="flex items-center w-full justify-between"
              style={{
                padding: '0 1rem',
              }}
            >
              <h3
                className="font-medium leading-4"
                style={{
                  fontSize: '0.7rem',
                }}
              >
                TOTAL CONCESSIONÁRIA <br /> + NEX ENERGY
              </h3>
              <h2
                style={{
                  fontSize: '1.3rem',
                }}
              >
                {convertToBRL(billTotalValueAddChargeTotalCharged)}
              </h2>
            </div>
            <div
              className="flex items-center w-full justify-between bg-primary"
              style={{
                padding: '.75rem 1rem',
              }}
            >
              <h3
                id="economy-report-bill-title"
                className="font-semibold leading-4"
                style={{
                  fontSize: '0.7rem',
                }}
              >
                ECONOMIA <br /> DESSE MÊS
              </h3>
              <h2
                id="economy-report-bill-value"
                className="font-semibold"
                style={{
                  fontSize: '1.3rem',
                }}
              >
                {convertToBRL(economyValue)}
              </h2>
            </div>
            <div
              className="flex gap-6 items-center justify-start bg-gray relative mt-2"
              style={{
                padding: '.75rem 1rem',
              }}
            >
              <div
                className="absolute bg-gray w-5 h-5 rotate-45"
                style={{
                  top: '-.5rem',
                  right: '13.5rem',
                }}
              />
              <div className="flex items-center justify-start gap-4">
                <MdHelp size={30} />
                <p
                  id="economy-report-bill-notice"
                  style={{
                    fontSize: '0.65rem',
                    lineHeight: '.75rem',
                  }}
                >
                  Economia calculada com base no valor projetado de como seria a
                  fatura do consumidor sem a NEX. Aproximadamente este valor seria{' '}
                  <strong>{convertToBRL(approximateChargeAmount)}</strong>
                </p>
              </div>
            </div>
          </div>
        </div>
        <div
          className="flex flex-col bg-primary gap-1"
          style={{
            width: '100%',
            maxWidth: '240px',
            padding: '1rem 1.5rem',
          }}
        >
          <h3
            id="economy-report-bill-title"
            className="font-bold text-center"
            style={{
              fontSize: '0.7rem',
            }}
          >
            $ CARTEIRA DE ECONOMIA
          </h3>
          {economyReports &&
            economyReports.map((item, index) => (
              <div
                key={index}
                className={classNames(
                  'flex w-full items-center justify-between pb-1 border-b',
                  {
                    'border-none': index === economyReports.length - 1,
                  }
                )}
                style={{
                  borderColor: '#5f5f5f39',
                }}
              >
                <p
                  id="economy-report-bill-month"
                  style={{
                    fontSize: '0.7rem',
                    lineHeight: '1.25rem',
                  }}
                >
                  {getMonthAndYear({
                    date: item.economyReferenceDate,
                    options: {
                      month: 'short',
                      timeZone: 'UTC',
                    },
                    spliter: '/',
                  }).toUpperCase()}
                </p>
                <p
                  id="economy-report-bill-month-value"
                  style={{
                    fontSize: '0.7rem',
                  }}
                >
                  {convertToBRL(item.economyValue)}
                </p>
              </div>
            ))}
          <div
            className="flex flex-col items-center justify-center bg-white"
            style={{
              padding: '.5rem .75rem',
            }}
          >
            <p
              id="economy-report-bill-title"
              style={{
                fontSize: '0.7rem',
                textAlign: 'center',
              }}
            >
              TOTAL ECONOMIZADO
            </p>
            <h1
              id="economy-report-bill-value-global"
              className="font-semibold leading-6"
              style={{
                fontSize: '1.3rem',
                textAlign: 'center',
              }}
            >
              {convertToBRL(
                String(
                  economyReports?.reduce((acc, item) => acc + +item.economyValue, 0)
                )
              )}
            </h1>
          </div>
        </div>
      </section>
      <footer className="w-56 h-2 bg-gray-dark200 bottom-6 left-6 absolute" />
    </div>
  );
}
